import type { Map as MapboxMap } from 'mapbox-gl';
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEAL_FILTER_TRACKING_DEBOUNCE_MS,
  DEALS_MIXPANEL_EVENTS,
  type DealStageFiltersAppliedPayload,
} from '@/features/deals/utils/mixpanelEvents';
import { useMixpanel } from '@/hooks/useMixpanel';
import {
  DEAL_STAGE_STYLES,
  DEAL_STAGES,
  type DealStageStyle,
  dealStagePosition,
  dealStageProgress,
  dealStageStyle,
  UNKNOWN_STAGE_STYLE,
} from '../constants';
import { addSvgIconViaCanvas } from '../utils/addSvgIconViaCanvas';
import {
  buildDealDotSvg,
  buildDealRingSvg,
  DEAL_DOT_CANVAS,
  dealDotIconId,
  dealRingIconId,
} from '../utils/dealMarkerIcon';
import { MapLayersService } from '../utils/MapLayersService';

/** Matches `buildDealRingSvg`'s canvas. */
const RING_ICON_SIZE = { width: 40, height: 44 };
/** The raster target has to match the canvas exactly or the mark stretches. */
const DOT_ICON_SIZE = { size: DEAL_DOT_CANVAS };

export interface DealStageRow {
  stage: string;
  style: DealStageStyle;
  visible: boolean;
}

/** Live stages in pipeline order, then the closed ones. */
export function inPipelineOrder(stages: readonly DealStageRow[]): DealStageRow[] {
  const order = (stage: string) => dealStagePosition(stage) || Number.MAX_SAFE_INTEGER;
  return [...stages].sort((a, b) => order(a.stage) - order(b.stage));
}

/**
 * Paints one dot + ring icon pair per `DEAL_STAGES` stage and lets the caller
 * toggle a stage's visibility.
 */
export function useDealsStageFilter(map: RefObject<MapboxMap | null>, ready: boolean) {
  // Lost loads hidden — the pipeline's only closed stage.
  const [hiddenStages, setHiddenStages] = useState<Set<string>>(
    () => new Set(DEAL_STAGE_STYLES.filter((style) => style.hollow).map((style) => style.stage)),
  );

  // ── Paint: dot + ring icons per stage ───────────────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !ready) return;

    let cancelled = false;
    const dotIdByStage: Record<string, string> = {};
    const ringIdByStage: Record<string, string> = {};
    (async () => {
      // The fallback first, and on its own: the layers reference it from the moment they are added,
      // and the per-stage loop below is ~25 awaits long. Registering it last would leave every
      // deal drawn from a missing image for as long as that takes.
      for (const stage of [UNKNOWN_STAGE_STYLE.stage, ...DEAL_STAGES]) {
        const style = dealStageStyle(stage);
        const dotId = dealDotIconId(stage);
        const ringId = dealRingIconId(stage);
        if (stage !== UNKNOWN_STAGE_STYLE.stage) {
          dotIdByStage[stage] = dotId;
          ringIdByStage[stage] = ringId;
        }

        if (!currentMap.hasImage(dotId)) {
          await addSvgIconViaCanvas(currentMap, dotId, buildDealDotSvg(style), DOT_ICON_SIZE);
        }
        if (!currentMap.hasImage(ringId)) {
          await addSvgIconViaCanvas(
            currentMap,
            ringId,
            buildDealRingSvg(style, dealStageProgress(stage)),
            RING_ICON_SIZE,
          );
        }
      }
      if (!cancelled) new MapLayersService(currentMap).paintDealsMarks(dotIdByStage, ringIdByStage);
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  // ── Filter: hide any stage the caller toggled off ────────────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !ready) return;

    new MapLayersService(currentMap).hideDealStages([...hiddenStages]);
  }, [ready, hiddenStages]);

  // ── Tracking: one emit per settled selection ────────────────────────────────
  // The scope is the toggle's, not the selection's, so the handlers record it and the debounced
  // emit below reads it — a set of hidden stages cannot say whether it came from one chip.
  const trackAction = useMixpanel((s) => s.trackAction);
  const scopeRef = useRef<DealStageFiltersAppliedPayload['scope']>('single');
  const untrackedRef = useRef(true);
  useEffect(() => {
    // The selection the panel opens on is nobody's choice, so the first reading is skipped rather
    // than reported as a filter nobody applied.
    if (untrackedRef.current) {
      untrackedRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const visibleStages = DEAL_STAGES.filter((stage) => !hiddenStages.has(stage));
      const payload: DealStageFiltersAppliedPayload = {
        visible_stages: visibleStages,
        visible_count: visibleStages.length,
        hidden_count: DEAL_STAGES.length - visibleStages.length,
        scope: scopeRef.current,
      };
      trackAction(DEALS_MIXPANEL_EVENTS.STAGE_FILTERS_APPLIED, payload);
    }, DEAL_FILTER_TRACKING_DEBOUNCE_MS);
    // Ticking four stages is four renders but one intent: each supersedes the pending emit, and a
    // panel left before the selection settled reports nothing.
    return () => clearTimeout(timer);
  }, [hiddenStages, trackAction]);

  // Stable identities: a fresh one per render would loop DealsStageSync's effect.
  const toggleStage = useCallback((stage: string) => {
    scopeRef.current = 'single';
    setHiddenStages((previous) => {
      const next = new Set(previous);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }, []);

  // Any stage visible hides the whole layer; nothing visible brings it all back.
  const toggleAllStages = useCallback(() => {
    scopeRef.current = 'all';
    setHiddenStages((previous) =>
      previous.size === DEAL_STAGES.length ? new Set() : new Set(DEAL_STAGES),
    );
  }, []);

  const stages: DealStageRow[] = useMemo(
    () =>
      DEAL_STAGES.map((stage) => ({
        stage,
        style: dealStageStyle(stage),
        visible: !hiddenStages.has(stage),
      })),
    [hiddenStages],
  );

  return { stages, toggleStage, toggleAllStages };
}
